import {
  SearchDialog as SearchDialogContainer,
  SearchResults,
  SearchSuggestions,
  SearchTable,
  SelectableTableRow,
} from "@opengov/components-nav-bar";
import { Box, Divider, Switch, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { IntlProvider, useIntl } from "react-intl";

import { DialogFooter } from "../DialogFooter/DialogFooter";
import { InfoBanner } from "../InfoBanner/InfoBanner";
import { getDescription, getTitle } from "../InfoBanner/InfoBanner.utils";
import messages from "../locales/en-US.json";
import { DEFAULT_LOCALE } from "../utils/intl";
import { getSearchFilterOptions, getUserBudgetIdStorageKey } from "../utils/SearchDialog.utils";

import { styles } from "./SearchDialog-styles";

import { SearchResultDisplayData } from "../SearchResultRow/SearchResultRow.types";
import { SearchResultRow } from "../SearchResultRow/SearchResultRow";
import { SearchFilterOption } from "./SearchDialog.types";
import { getDecodedBudgetId } from "../utils/encodeAndDecode.utils";
import { SearchQueryVariables } from "../queries/SearchQuery";
import { useFetchSearchResultsQuery } from "../hooks/useFetchSearchResultsQuery";
import { useFetchBudgetQuery } from "../hooks/useFetchBudgetQuery";
import { addToRecentlyViewedSearchResults, getRecentlyViewedSearchResults } from "../utils/recentResultsUtils";
import { getValidRecordsDisplayData } from "../utils/SearchResultRow.utils";
import { MINIMUM_SEARCH_TERM_LENGTH } from "../constants/SearchDialog.constants";

export type OgAssistSearchDialogProps = {
  entityId: number;
  subdomain: string;
  userUuid: string;
  isWorkforceAuthorized?: boolean;
  /** @deprecated No longer used. Will be removed in a future version. */
  entityFeaturePermissions?: string[];
  budgetId?: number;
  open: boolean;
  onClose?: () => void;
};

const SearchDialog = ({
  budgetId,
  entityId,
  open,
  subdomain,
  userUuid,
  isWorkforceAuthorized,
  onClose,
}: OgAssistSearchDialogProps) => {
  const intl = useIntl();
  const [searchInput, setSearchInput] = useState<string>("");

  const searchFilterOptions = useMemo<SearchFilterOption[]>(() => {
    return getSearchFilterOptions(intl).filter((filter) => {
      if (filter.checkPermissions) {
        return filter.checkPermissions({
          isWorkforceAuthorized: isWorkforceAuthorized ?? false,
        });
      }
      return true;
    });
  }, [isWorkforceAuthorized, intl]);

  const budgetIdToSearch = useMemo<number | null>(() => {
    if (budgetId) return budgetId;
    const latestBudgetIdStorageKey = getUserBudgetIdStorageKey(subdomain, userUuid);
    const latestBudgetId = localStorage.getItem(latestBudgetIdStorageKey);
    return latestBudgetId ? Number(getDecodedBudgetId(latestBudgetId)) : null;
  }, [subdomain, budgetId, userUuid]);

  const { budget } = useFetchBudgetQuery({ budgetId: budgetIdToSearch });

  const [searchFilterValue, setSearchFilterValue] = useState<SearchFilterOption>(searchFilterOptions[0]);
  const [searchAcrossAllBudgets, setSearchAcrossAllBudgets] = useState<boolean>(false);
  const hasMinSearchTerm = searchInput.length > MINIMUM_SEARCH_TERM_LENGTH;

  const variables: SearchQueryVariables = useMemo<SearchQueryVariables>(
    () => ({
      query: searchInput,
      documentType: searchFilterValue.value,
      entityId,
      budgetId: !searchAcrossAllBudgets ? budgetIdToSearch : undefined,
    }),
    [searchInput, searchFilterValue, entityId, searchAcrossAllBudgets, budgetIdToSearch],
  );

  const {
    isLoading: searchIsLoading,
    searchResults,
    searchError = null,
  } = useFetchSearchResultsQuery({ variables, searchInput, intl });

  const onSearchFilterValueChange = (filterOption: string) => {
    const selectedFilterOption = searchFilterOptions.find(({ value }) => filterOption === value.toString());
    selectedFilterOption ? setSearchFilterValue(selectedFilterOption) : setSearchFilterValue(searchFilterOptions[0]);
  };

  const recentlyViewedSearchResults = useMemo(() => {
    if (searchInput) return [];
    const recent = getRecentlyViewedSearchResults(entityId);
    return getValidRecordsDisplayData(recent, intl);
  }, [entityId, intl, searchInput]);

  const onSearchResultClick = (entityId: number, data: SearchResultDisplayData) => {
    window.open(data.url, "_blank");
    addToRecentlyViewedSearchResults(entityId, data.originalRecord);
  };

  return (
    <SearchDialogContainer
      open={open}
      onClose={onClose}
      searchInput={searchInput}
      onSearchInputChange={setSearchInput}
      placeholder={intl.formatMessage({
        defaultMessage: "Type a search term",
      })}
      searchFilterOptions={searchFilterOptions}
      activeSearchFilter={searchFilterValue.value}
      onActiveSearchFilterChange={onSearchFilterValueChange}
      searchResults={searchResults}
      searchSuggestions={recentlyViewedSearchResults}
      onSelectSearchResult={(data: SearchResultDisplayData) => {
        onSearchResultClick(entityId, data);
      }}
      onSelectSearchSuggestion={(data: SearchResultDisplayData) => {
        onSearchResultClick(entityId, data);
      }}
      isLoading={searchIsLoading}
    >
      {searchFilterValue.supportInBudgetSearch && budgetIdToSearch ? (
        <Box sx={styles.switch}>
          <Typography htmlFor="search-across-all-budgets-toggle" component="label" variant="body2">
            {intl.formatMessage({
              defaultMessage: "Search across all budgets",
            })}
          </Typography>
          <Switch
            id="search-across-all-budgets-toggle"
            data-test="searchAcrossAllBudgetsToggle"
            size="small"
            onChange={(event) => {
              setSearchAcrossAllBudgets(event.target.checked);
            }}
            checked={searchAcrossAllBudgets}
          />
        </Box>
      ) : null}

      <SearchSuggestions>
        {recentlyViewedSearchResults.length ? (
          <SearchTable
            title={intl.formatMessage({
              defaultMessage: "Recently Viewed",
            })}
            gridTemplateColumns="1fr"
          >
            {({ rowData }: { rowData: SearchResultDisplayData }) => (
              <SelectableTableRow key={rowData.id}>
                <SearchResultRow key={rowData.id} data={rowData} />
              </SelectableTableRow>
            )}
          </SearchTable>
        ) : (
          <InfoBanner
            title={getTitle({
              intl,
              hasMinSearchTerm,
              searchError,
            })}
            description={getDescription({
              intl,
              searchFilterValue,
              searchAcrossAllBudgets,
              budgetName: budget?.title || null,
              hasMinSearchTerm,
              searchError,
            })}
          />
        )}
      </SearchSuggestions>
      <SearchResults
        noResultsComponent={
          <InfoBanner
            title={getTitle({
              intl,
              hasMinSearchTerm,
              searchError,
            })}
            description={getDescription({
              intl,
              searchFilterValue,
              searchAcrossAllBudgets,
              budgetName: budget?.title || null,
              hasMinSearchTerm,
              searchError,
            })}
          />
        }
      >
        <SearchTable
          title={intl.formatMessage({
            defaultMessage: "Results",
          })}
          gridTemplateColumns="1fr"
        >
          {({ rowData }: { rowData: SearchResultDisplayData }) => (
            <SelectableTableRow key={rowData.id}>
              <SearchResultRow key={rowData.id} data={rowData} />
            </SelectableTableRow>
          )}
        </SearchTable>
      </SearchResults>
      <Divider sx={styles.divider} />
      <DialogFooter />
    </SearchDialogContainer>
  );
};

export const OgAssistSearchDialog = (props: OgAssistSearchDialogProps) => {
  return (
    <IntlProvider locale={DEFAULT_LOCALE} messages={messages}>
      <SearchDialog {...props} />
    </IntlProvider>
  );
};
